import { useCallback, useMemo, VFC } from 'react';

import ConnectedTagSelect from 'dashboard/components/ConnectedTagSelect';
import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import { taggableObjectsFor, UsableObjectConfig } from 'dashboard/lib/workflows/objects';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowActionCopyTagsV1 } from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';

interface ParamsCopyTagsV1Props {
  editable: boolean;
  errors: WorkflowError[];
  focusStep: (token: string) => void;
  params: WorkflowActionCopyTagsV1['params'];
  usableObjects: UsableObjectConfig[];
  onMenuClose: () => void;
  updateActionParams: (newParams: WorkflowActionCopyTagsV1['params']) => void;
}

const ParamsCopyTagsV1: VFC<ParamsCopyTagsV1Props> = ({
  editable,
  errors,
  focusStep,
  params,
  usableObjects,
  onMenuClose,
  updateActionParams,
}) => {
  const possibleObjects = useMemo(() => taggableObjectsFor(usableObjects), [usableObjects]);

  const possibleFromObjects = useMemo(
    () => possibleObjects.filter((object) => object.value !== params['to-object']),
    [possibleObjects, params],
  );

  const possibleToObjects = useMemo(
    () => possibleObjects.filter((object) => object.value !== params['from-object']),
    [possibleObjects, params],
  );

  const selectFromObject = useCallback(
    (object: string) => {
      updateActionParams({
        ...params,
        'from-object': object,
        'to-object': object === params['to-object'] ? '' : params['to-object'],
      });
    },
    [params, updateActionParams],
  );

  const selectToObject = useCallback(
    (object: string) => {
      updateActionParams({
        ...params,
        'to-object': object,
        'from-object': object === params['from-object'] ? '' : params['from-object'],
      });
    },
    [params, updateActionParams],
  );

  const changeExcludeTags = useCallback(
    (tagIds: string[]) => {
      updateActionParams({
        ...params,
        'exclude-tags': tagIds,
      });
    },
    [params, updateActionParams],
  );

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionCopyTagsV1['params']>(errors);

  return (
    <>
      <Spacer height={2} />
      <ParamLabel
        helpText='Tags are only copied if they exist on this object at the time this workflow step executes'
        labelText='From'
        htmlFor='params-copy-tags__from-object-input'
      />
      <ReactSelect
        value={params['from-object']}
        options={possibleFromObjects}
        onChange={selectFromObject}
        id='params-copy-tags__from-object'
        disabled={!editable}
        hasError={hasErrorFor('from-object')}
        onFocusOption={focusStep}
        onMenuClose={onMenuClose}
      />
      {renderErrorMessageFor('from-object')}
      <Spacer height={2} />
      <ParamLabel labelText='To' htmlFor='params-copy-tags__to-object-input' />
      <ReactSelect
        value={params['to-object']}
        options={possibleToObjects}
        onChange={selectToObject}
        id='params-copy-tags__to-object'
        disabled={!editable}
        hasError={hasErrorFor('to-object')}
        onFocusOption={focusStep}
        onMenuClose={onMenuClose}
      />
      {renderErrorMessageFor('to-object')}
      <Spacer height={2} />
      <ParamLabel
        helpText='Tags will not be copied by this step if they are excluded here'
        labelText='Exclude Tags'
        htmlFor='params-copy-tags__exclude-tags-input'
      />
      <ConnectedTagSelect
        size='md'
        placeholder='To create a new tag start typing'
        value={params['exclude-tags'] || []}
        tagScope='all'
        onChange={changeExcludeTags}
        id='params-copy-tags__exclude-tags'
        disabled={!editable}
        hasError={hasErrorFor('exclude-tags')}
      />
    </>
  );
};

export default ParamsCopyTagsV1;
